// Admin › Period & Operations › Evaluation integrity. Read-only view of the period
// certification audit that gates Evaluation Responsibility rating. It explains why a
// period is or is not certifiable; it never backfills or edits registry records.
import { useEffect, useState } from "react";
import UiIcon from "../components/UiIcon";
import { useResource } from "./hooks";
import { evaluationResponsibilityCopy } from "../components/routine/evaluationResponsibilityCopy";

const CATEGORY_ORDER = ["PERIOD", "UNREGISTERED", "UNRECONCILED", "ASSIGNMENT_CONFLICT", "OWNER", "KPI_SCHEDULE", "RECORD"];

export default function ResponsibilityIntegrity({ language, periods = [], activePeriodId }) {
    const c = evaluationResponsibilityCopy(language);
    const a = c.admin;
    const [periodId, setPeriodId] = useState(activePeriodId || periods[0]?.id || "");
    useEffect(() => { if (!periodId && (activePeriodId || periods[0]?.id)) setPeriodId(activePeriodId || periods[0]?.id); }, [periodId, activePeriodId, periods]);
    const { data, loading, error, reload } = useResource(periodId ? `/api/evaluation-responsibility/registry/integrity?periodId=${encodeURIComponent(periodId)}` : null);
    if (!periods.length) return <p className="cvi-xp-muted">{a.noPeriods}</p>;
    const categories = data?.summary?.blockerCategories || {};
    const blockers = CATEGORY_ORDER.filter(key => categories[key]);
    const bySource = Object.entries(data?.summary?.missingBySource || {}).sort((x, y) => y[1] - x[1]);
    return <div className="cvi-er-integrity">
        <p className="cvi-xp-muted">{a.intro}</p>
        <div className="cvi-xp-form-grid">
            <label className="cvi-xp-field"><span>{a.period}</span>
                <select value={periodId} onChange={e => setPeriodId(e.target.value)}>{periods.map(p => <option key={p.id} value={p.id}>{p.label || p.name}</option>)}</select>
            </label>
        </div>
        {error ? <div className="adm-callout is-danger" role="alert"><UiIcon name="warning" size={15} /><div>{a.loadError} <button type="button" className="adm-btn adm-btn--sm adm-btn--ghost" onClick={reload}>{c.retry}</button></div></div>
        : !data ? <div className="cvi-er-metrics is-loading" aria-busy={loading || undefined} aria-label={c.loading}><span /><span /><span /></div>
        : <>
            <dl className="cvi-er-metrics cvi-er-integrity-stats">
                <div><dt>{a.status}</dt><dd><span className={`adm-badge ${data.sourcesComplete ? "adm-badge--success" : "adm-badge--danger"}`}>{data.sourcesComplete ? a.certified : a.notCertified}</span></dd></div>
                <div><dt>{a.expected}</dt><dd>{data.expectedCount}</dd></div>
                <div><dt>{a.registered}</dt><dd>{data.registeredCount}</dd></div>
            </dl>
            <p className="cvi-routine-status-line" role="note"><UiIcon name={data.eligibility?.eligible ? "check" : "shield"} size={14} />
                <span><strong>{a.eligibility}:</strong> {data.eligibility?.eligible ? a.eligible : a.ineligible(data.eligibility?.periodStatus, data.eligibility?.cutoverConfigured)}</span></p>
            <section aria-labelledby="cvi-er-blockers">
                <h3 id="cvi-er-blockers" className="cvi-routine-label">{a.blockers}</h3>
                {blockers.length ? <table><tbody>
                    {blockers.map(key => <tr key={key}><td>{a.categories[key]}</td><td>{categories[key]}</td></tr>)}
                </tbody></table> : <p className="cvi-xp-muted">{a.noBlockers}</p>}
            </section>
            {bySource.length > 0 && <section aria-labelledby="cvi-er-by-source">
                <h3 id="cvi-er-by-source" className="cvi-routine-label">{a.bySource}</h3>
                <table><tbody>{bySource.map(([source, count]) => <tr key={source}><td>{c.sources[source] || source}</td><td>{count}</td></tr>)}</tbody></table>
            </section>}
            <p className="cvi-er-footnote">{a.readOnly}</p>
        </>}
    </div>;
}
